<template>
  <div class="extension-builder">
    <b-card>
      <div class="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 class="mb-0">
            <b-icon icon="puzzle" class="mr-2"></b-icon>
            {{ $t('extensionBuilder') }}
          </h4>
          <p class="text-muted mb-0 mt-1">{{ $t('extensionBuilderDescription') }}</p>
        </div>
        <div>
          <b-button variant="outline-secondary" @click="importExtension" class="mr-2">
            <b-icon icon="upload" class="mr-1"></b-icon>
            {{ $t('import') }}
          </b-button>
          <b-button variant="success" @click="showInstallModal" class="mr-2">
            <b-icon icon="box-arrow-in-down" class="mr-1"></b-icon>
            {{ $t('installToComponent') }}
          </b-button>
          <b-dropdown split variant="primary" right @click="exportExtension">
            <template #button-content>
              <b-icon icon="download" class="mr-1"></b-icon>
              {{ $t('export') }}
            </template>
            <b-dropdown-item @click="copyJsonToClipboard">
              <b-icon icon="clipboard" class="mr-1"></b-icon>
              {{ $t('copyJsonToClipboard') }}
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>

      <!-- Extension Metadata Form -->
      <b-row class="mb-4">
        <b-col cols="12" lg="6">
          <b-form-group :label="$t('extensionName')" label-for="extension-name" :description="$t('extensionNameRules')">
            <b-form-input
              id="extension-name"
              v-model="extensionName"
              :placeholder="$t('enterExtensionName')"
              :state="extension.name ? /^[a-z0-9_]+$/.test(extension.name) : null"
              required
            ></b-form-input>
          </b-form-group>
        </b-col>
        <b-col cols="12" lg="6">
          <b-form-group :label="$t('version')" label-for="extension-version">
            <b-form-input
              id="extension-version"
              v-model="extension.version"
              :placeholder="$t('enterVersion')"
              required
            ></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Tabs for Fields, Functions, Workflows, Config -->
      <b-tabs content-class="mt-3" fill>
        <!-- Fields Tab -->
        <b-tab :title="$t('fields')" active>
          <FieldsV2
            v-model="extension.fields"
            :config="fieldsConfig"
            :component="mockComponent"
          />
        </b-tab>

        <!-- Functions Tab -->
        <b-tab :title="$t('functions.title')">
          <Functions
            v-model="extension.functions"
            :id="extension.name"
            :config="{}"
            :extensions="[]"
            :idl="extensionIDL"
          />
        </b-tab>

        <!-- Workflows Tab -->
        <b-tab :title="$t('workflows')">
          <Events
            :key="workflowKey"
            v-model="extension.workflows"
            :schema="''"
            :fields="extension.fields"
            :event-names="defaultEventNames"
            :uuid="extension.name"
            :disable-dry-run="true"
            :readonly="false"
          />
        </b-tab>

        <!-- Config Tab -->
        <b-tab :title="$t('configuration')">
          <b-alert show variant="info" class="mb-3">
            <b-icon icon="info-circle" class="mr-2"></b-icon>
            {{ $t('extensionConfigDescription') }}
          </b-alert>
          <CodeEditor
            v-model="extensionConfigString"
            language="json"
            :height="400"
            :withButton="false"
          />
        </b-tab>

        <!-- IDL Tab -->
        <b-tab :title="$t('idl.title')">
          <b-card class="mb-3">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <p class="text-muted mb-0">{{ $t('idl.extensionDescription') }}</p>
              <a href="https://docs.protokol.io/components/idl/" target="_blank" rel="noopener" class="text-nowrap ml-3">
                <b-icon icon="book" class="mr-1"></b-icon><small>{{ $t('documentation') }}</small>
              </a>
            </div>
            <CodeEditor
              v-model="extensionIdlString"
              language="json"
              :height="500"
              :withButton="false"
            />
          </b-card>
        </b-tab>
      </b-tabs>
    </b-card>

    <!-- Install to Component Modal -->
    <main-dialog v-model="installModal.show" :title="$t('installToComponent')" @ok="installToComponent" id="install-extension-modal">
      <b-form-group :label="$t('selectComponent')" :description="$t('selectComponentToInstall')">
        <v-select
          v-model="installModal.selected"
          :options="activeComponents"
          label="text"
          :reduce="opt => opt"
          :placeholder="$t('selectComponent')"
        />
      </b-form-group>
    </main-dialog>

    <!-- Import Modal -->
    <main-dialog v-model="importModal.show" :title="$t('importExtension')" @ok="handleImport" size="lg">
      <b-tabs content-class="mt-3">
        <b-tab :title="$t('uploadFile')" active>
          <b-form-file
            v-model="importModal.file"
            :placeholder="$t('chooseFile')"
            accept=".json"
            @change="handleFileUpload"
          ></b-form-file>
        </b-tab>
        <b-tab :title="$t('pasteJson')">
          <CodeEditor
            v-model="importModal.jsonString"
            language="json"
            :height="300"
            :withButton="false"
          />
        </b-tab>
      </b-tabs>
    </main-dialog>
  </div>
</template>

<script>
import FieldsV2 from '@/components/base/Settings/FieldsV2'
import Functions from '@/components/base/Settings/Functions'
import Events from '@/components/base/Settings/Events'
import CodeEditor from '@/components/form/Editor/Code'
import VSelect from 'vue-select'

export default {
  name: 'ExtensionBuilder',
  components: {
    FieldsV2,
    Functions,
    Events,
    CodeEditor,
    VSelect
  },
  data() {
    return {
      extension: {
        name: '',
        version: '1.0.0',
        is_active: true,
        fields: [],
        functions: [],
        workflows: [],
        config: {},
        idl: {}
      },
      fieldsConfig: {
        hideGroup: true,
        hideProtected: false,
        hideVisible: false,
        fieldSize: 4,
        custom: false,
        dependencyProtected: false,
        version: 0,
        disableSchemas: false
      },
      importModal: {
        show: false,
        file: null,
        jsonString: ''
      },
      installModal: {
        show: false,
        selected: null
      },
      workflowKey: 0,
      defaultEventNames: [
        {
          name: 'Before::Create',
          label: {
            locales: {
              en_US: 'Before create',
              sr: 'Pre kreiranja'
            }
          }
        },
        {
          name: 'Create',
          label: {
            locales: {
              en_US: 'Create',
              sr: 'Kreirano'
            }
          }
        },
        {
          name: 'Before::Update',
          label: {
            locales: {
              en_US: 'Before update',
              sr: 'Pre ažuriranja'
            }
          }
        },
        {
          name: 'Update',
          label: {
            locales: {
              en_US: 'Update',
              sr: 'Ažurirano'
            }
          }
        },
        {
          name: 'Before::Delete',
          label: {
            locales: {
              en_US: 'Before delete',
              sr: 'Pre brisanja'
            }
          }
        },
        {
          name: 'Delete',
          label: {
            locales: {
              en_US: 'Delete',
              sr: 'Obrisano'
            }
          }
        },
        {
          name: 'Recycle',
          label: {
            locales: {
              en_US: 'Recycle',
              sr: 'Recikliraj'
            }
          }
        }
      ]
    }
  },
  computed: {
    extensionName: {
      get() {
        return this.extension.name
      },
      set(val) {
        this.extension.name = val.toLowerCase().replace(/[\s-]+/g, '_').replace(/[^a-z0-9_]/g, '')
      }
    },
    extensionConfigString: {
      get() {
        return JSON.stringify(this.extension.config, null, 2)
      },
      set(value) {
        try {
          this.extension.config = JSON.parse(value)
        } catch (e) {
          // Invalid JSON, don't update
        }
      }
    },
    extensionIdlString: {
      get() {
        return JSON.stringify(this.extension.idl || {}, null, 2)
      },
      set(value) {
        try {
          this.$set(this.extension, 'idl', JSON.parse(value))
        } catch (e) {
          // Invalid JSON, don't update
        }
      }
    },
    activeComponents() {
      const cm = this.$store.state.components.componentsMenu || []
      return cm.map(c => ({
        text: c.name || c.getRef(),
        ref: c.getRef(),
        version: localStorage.getItem("current_env") === "live" ? c.public_version : c.dev_version
      }))
    },
    // Mock component for FieldsV2 to work properly
    mockComponent() {
      return {
        settings: {
          extensions: []
        }
      }
    },
    // Extract the default-schema IDL for passing to Functions
    extensionIDL() {
      const idlMap = this.extension.idl
      if (!idlMap || typeof idlMap !== 'object') return null
      return idlMap['default'] || idlMap[Object.keys(idlMap)[0]] || null
    }
  },
  methods: {
    showInstallModal() {
      if (!this.extension.name || !this.extension.version) {
        this.$bvToast.toast(this.$t('pleaseEnterNameAndVersion'), {
          title: this.$t('error'),
          variant: 'warning'
        })
        return
      }
      this.installModal.selected = null
      this.installModal.show = true
    },

    async installToComponent() {
      const selected = this.installModal.selected
      if (!selected) return

      const popup = this.$loadingPopup()
      try {
        const extensionData = {
          name: this.extension.name,
          version: this.extension.version,
          is_active: this.extension.is_active,
          fields: this.extension.fields,
          functions: this.extension.functions,
          workflows: this.extension.workflows,
          config: this.extension.config,
          idl: this.extension.idl
        }

        await this.$http.post(
          `/v4/system/component/${selected.ref}/${selected.version}/extensions`,
          extensionData
        )

        this.installModal.show = false
        this.$bvToast.toast(
          this.$t('extensionInstalledToComponent', { component: selected.text }),
          { title: this.$t('success'), variant: 'success' }
        )
      } catch (error) {
        const message = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : this.$t('failedToInstallExtension')
        this.$bvToast.toast(message, { title: this.$t('error'), variant: 'danger' })
      } finally {
        popup.hide()
      }
    },

    exportExtension() {
      // Validate required fields
      if (!this.extension.name || !this.extension.version) {
        this.$bvToast.toast(this.$t('pleaseEnterNameAndVersion'), {
          title: this.$t('error'),
          variant: 'warning'
        })
        return
      }

      // Create export data
      const exportData = {
        name: this.extension.name,
        version: this.extension.version,
        is_active: this.extension.is_active,
        fields: this.extension.fields,
        functions: this.extension.functions,
        workflows: this.extension.workflows,
        config: this.extension.config,
        idl: this.extension.idl
      }

      // Download as JSON file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${this.extension.name}-${this.extension.version}.json`
      link.click()
      URL.revokeObjectURL(url)

      this.$bvToast.toast(this.$t('extensionExportedSuccess'), {
        title: this.$t('success'),
        variant: 'success'
      })
    },

    copyJsonToClipboard() {
      if (!this.extension.name || !this.extension.version) {
        this.$bvToast.toast(this.$t('pleaseEnterNameAndVersion'), {
          title: this.$t('error'),
          variant: 'warning'
        })
        return
      }

      const exportData = {
        name: this.extension.name,
        version: this.extension.version,
        is_active: this.extension.is_active,
        fields: this.extension.fields,
        functions: this.extension.functions,
        workflows: this.extension.workflows,
        config: this.extension.config,
        idl: this.extension.idl
      }

      const jsonString = JSON.stringify(exportData, null, 2)

      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(jsonString).then(() => {
          this.$bvToast.toast(this.$t('jsonCopiedToClipboard'), {
            title: this.$t('success'),
            variant: 'success'
          })
        }).catch(() => {
          this.$bvToast.toast(this.$t('copyToClipboardFailed'), {
            title: this.$t('error'),
            variant: 'danger'
          })
        })
      } else {
        // Fallback for non-secure contexts
        const textArea = document.createElement('textarea')
        textArea.value = jsonString
        textArea.style.position = 'fixed'
        textArea.style.opacity = '0'
        document.body.appendChild(textArea)
        textArea.focus()
        textArea.select()
        try {
          document.execCommand('copy')
          this.$bvToast.toast(this.$t('jsonCopiedToClipboard'), {
            title: this.$t('success'),
            variant: 'success'
          })
        } catch {
          this.$bvToast.toast(this.$t('copyToClipboardFailed'), {
            title: this.$t('error'),
            variant: 'danger'
          })
        } finally {
          document.body.removeChild(textArea)
        }
      }
    },

    importExtension() {
      this.importModal.show = true
      this.importModal.file = null
      this.importModal.jsonString = ''
    },
    handleFileUpload(event) {
      const file = event.target.files[0]
      if (!file) return

      const reader = new FileReader()
      reader.onload = (e) => {
        this.importModal.jsonString = e.target.result
      }
      reader.readAsText(file)
    },
    handleImport() {
      try {
        const importedData = JSON.parse(this.importModal.jsonString)
        
        // Validate required fields
        if (!importedData.name || !importedData.version) {
          throw new Error(this.$t('invalidExtensionFormat'))
        }

        // Load extension data with proper reactivity
        this.$set(this.extension, 'name', importedData.name)
        this.$set(this.extension, 'version', importedData.version)
        this.$set(this.extension, 'is_active', importedData.is_active !== undefined ? importedData.is_active : true)
        this.$set(this.extension, 'fields', importedData.fields || [])
        this.$set(this.extension, 'functions', importedData.functions || [])
        this.$set(this.extension, 'workflows', importedData.workflows || [])
        this.$set(this.extension, 'config', importedData.config || {})
        this.$set(this.extension, 'idl', importedData.idl || {})
        
        // Force re-render of workflows component
        this.workflowKey++

        this.$bvToast.toast(this.$t('extensionImported'), {
          title: this.$t('success'),
          variant: 'success'
        })
      } catch (error) {
        this.$bvToast.toast(error.message || this.$t('invalidJsonFormat'), {
          title: this.$t('error'),
          variant: 'danger'
        })
      }
    }
  },
  mounted() {
    // Check if there's an extension to import from localStorage
    const extensionToImport = localStorage.getItem('extensionBuilderImport')
    if (extensionToImport) {
      try {
        const importedData = JSON.parse(extensionToImport)
        
        // Load extension data with proper reactivity
        this.$set(this.extension, 'name', importedData.name)
        this.$set(this.extension, 'version', importedData.version)
        this.$set(this.extension, 'is_active', importedData.is_active !== undefined ? importedData.is_active : true)
        this.$set(this.extension, 'fields', importedData.fields || [])
        this.$set(this.extension, 'functions', importedData.functions || [])
        this.$set(this.extension, 'workflows', importedData.workflows || [])
        this.$set(this.extension, 'config', importedData.config || {})
        this.$set(this.extension, 'idl', importedData.idl || {})
        
        // Force re-render of workflows component
        this.workflowKey++
        
        // Clear the localStorage
        localStorage.removeItem('extensionBuilderImport')
        
        this.$bvToast.toast(this.$t('extensionImported'), {
          title: this.$t('success'),
          variant: 'success'
        })
      } catch (error) {
        console.error('Error loading extension:', error)
        localStorage.removeItem('extensionBuilderImport')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.extension-builder {
  padding: 1rem;

  ::v-deep .card {
    border: 1px solid var(--border-color);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  ::v-deep .nav-tabs {
    border-bottom: 2px solid var(--border-color);

    .nav-link {
      color: var(--text-muted);
      font-weight: 500;
      padding: 0.75rem 1.5rem;

      &.active {
        color: var(--primary);
        border-bottom: 2px solid var(--primary);
      }

      &:hover:not(.active) {
        color: var(--text-color);
      }
    }
  }
}
</style>

<style lang="scss">
#install-extension-modal .mp-dialog-body {
  overflow: visible;
}
</style>
